import { Form } from 'antd';
import { useMemo } from 'react';
import { useKimLhcAutoCalculate, BaseCalculationPayload } from '@/components/views/Report/KimLhc/hooks/useKimLhcAutoCalculate';
import { FieldNamesEnum } from '@/components/views/Report/KimLhc/types';

interface CalculatedScores {
	frequencyScore: number;
	effectiveWeight: number;
	manWeightScore: number;
	womanWeightScore: number;
	cargoHandlingConditionScore: number;
	manScore: number;
	womanScore: number;
}

interface FrequencyFormData {
	frequency?: number;
	weight?: number;
	inclined_weight?: boolean;
	transporting_cargo_in_pairs?: boolean;
	cargo_handling_condition?: string;
}

interface CalculateScoresResponse {
	frequency_score: number;
	effective_weight: number;
	man_weight_score: number;
	woman_weight_score: number;
	cargo_handling_condition_score: number;
	man_score: number;
	woman_score: number;
}

const INITIAL_SCORES: CalculatedScores = {
	frequencyScore: 0,
	effectiveWeight: 0,
	manWeightScore: 0,
	womanWeightScore: 0,
	cargoHandlingConditionScore: 0,
	manScore: 0,
	womanScore: 0
};

function buildPayload(formData: FrequencyFormData, basePayload: BaseCalculationPayload) {
	return {
		...basePayload,
		frequency: formData.frequency,
		weight: formData.weight,
		inclined_weight: formData.inclined_weight,
		transporting_cargo_in_pairs: formData.transporting_cargo_in_pairs,
		cargo_handling_condition: formData.cargo_handling_condition
	};
}

function transformScores(scores: CalculateScoresResponse): CalculatedScores {
	return {
		frequencyScore: scores.frequency_score,
		effectiveWeight: scores.effective_weight,
		manWeightScore: scores.man_weight_score,
		womanWeightScore: scores.woman_weight_score,
		cargoHandlingConditionScore: scores.cargo_handling_condition_score,
		manScore: scores.man_score,
		womanScore: scores.woman_score
	};
}

export function useAutoCalculate() {
	const form = Form.useFormInstance();
	const FREQUENCY_GROUP = FieldNamesEnum.FREQUENCY;

	const frequency = Form.useWatch([FREQUENCY_GROUP, 'frequency'], form);
	const weight = Form.useWatch([FREQUENCY_GROUP, 'weight'], form);
	const inclinedWeight = Form.useWatch([FREQUENCY_GROUP, 'inclined_weight'], form);
	const transportingCargoInPairs = Form.useWatch([FREQUENCY_GROUP, 'transporting_cargo_in_pairs'], form);
	const cargoHandlingCondition = Form.useWatch([FREQUENCY_GROUP, 'cargo_handling_condition'], form);

	const watchedValues = useMemo(
		() => ({
			frequency,
			weight,
			inclined_weight: inclinedWeight,
			transporting_cargo_in_pairs: transportingCargoInPairs,
			cargo_handling_condition: cargoHandlingCondition
		}),
		[frequency, weight, inclinedWeight, transportingCargoInPairs, cargoHandlingCondition]
	);

	return useKimLhcAutoCalculate<FrequencyFormData, CalculatedScores, CalculateScoresResponse>({
		watchedValues,
		initialScores: INITIAL_SCORES,
		buildPayload,
		transformScores
	});
}
